import { useEffect, useState } from "react";
import { Heart, X, Sparkles, MapPin, Eye, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { useDeck, useSwipe, useMarkSeen } from "../hooks/useDating";
import { useProfileMe } from "../hooks/useProfile";

export function Deck() {
  const { data: cards = [], isLoading } = useDeck();
  const { data: me } = useProfileMe();
  const swipe = useSwipe();
  const markSeen = useMarkSeen();
  const [index, setIndex] = useState(0);
  const [matchModal, setMatchModal] = useState<string | null>(null);

  const current = cards[index];

  useEffect(() => {
    if (current && !current.entryId.startsWith("seen-")) {
      markSeen.mutate(current.entryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.entryId]);

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
        </div>
      </AppShell>
    );
  }

  if (!current) {
    return (
      <AppShell>
        <section className="container flex min-h-[70vh] max-w-md flex-col items-center justify-center text-center">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-brand-50 text-brand-500">
            <Heart className="h-8 w-8" fill="currentColor" />
          </span>
          <h1 className="mt-6 text-2xl font-bold tracking-tight">
            Fürs Heute war's das.
          </h1>
          <p className="mt-2 max-w-xs text-ink-500">
            Komm morgen zurück — wir bereiten frische Vorschläge vor, die
            wirklich zu dir passen.
          </p>
        </section>
      </AppShell>
    );
  }

  async function decide(decision: "like" | "pass") {
    if (!current) return;
    try {
      const result = await swipe.mutateAsync({
        targetId: current.profileId,
        decision,
      });
      if (result.matched && result.matchId) {
        setMatchModal(result.matchId);
      }
    } catch {
      // Fehler ignorieren, einfach zur nächsten Karte
    }
    setIndex((i) => i + 1);
  }

  return (
    <AppShell>
      <section className="container max-w-md pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-bold tracking-tight">Entdecken</h1>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1.5 text-xs font-semibold text-ink-700">
            <Eye className="h-3.5 w-3.5 text-brand-500" />
            {me?.sightingsToday ?? 0} Sichtungen heute
          </span>
        </div>

        <div className="relative aspect-[3/4] overflow-hidden rounded-[2rem] shadow-card">
          {current.photos[0] ? (
            <img
              src={current.photos[0].url}
              alt={current.displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-brand-gradient" />
          )}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink-900/80 via-ink-900/40 to-transparent p-6 pt-20 text-white">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">
                  {current.displayName}, {current.age}
                </h2>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-white/90">
                  <MapPin className="h-3.5 w-3.5" />
                  {current.city} · ca. {current.distanceKm} km
                </p>
              </div>
              <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
                {Math.round(current.valuesSim * 100)}% Match
              </span>
            </div>
            {current.bio && (
              <p className="mt-3 text-sm text-white/90">{current.bio}</p>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-5">
          <button
            type="button"
            onClick={() => decide("pass")}
            disabled={swipe.isPending}
            aria-label="Nope"
            className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-border bg-white text-ink-500 shadow-soft transition active:scale-95 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={() => decide("like")}
            disabled={swipe.isPending}
            aria-label="Like"
            className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-gradient text-white shadow-glow transition active:scale-95 disabled:opacity-50"
          >
            {swipe.isPending ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Heart className="h-7 w-7" fill="currentColor" />
            )}
          </button>
          <button
            type="button"
            aria-label="Werte zeigen"
            className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-border bg-white text-sunset-500 shadow-soft transition active:scale-95"
          >
            <Sparkles className="h-6 w-6" />
          </button>
        </div>
      </section>

      {matchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/70 p-6 backdrop-blur-sm">
          <div className="card w-full max-w-sm p-8 text-center">
            <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
              <Heart className="h-8 w-8 animate-heart-pulse text-white" fill="currentColor" />
            </div>
            <h2 className="mt-5 text-2xl font-bold tracking-tight">
              Es ist ein Match!
            </h2>
            <p className="mt-2 text-ink-500">
              Du und {current.displayName} habt euch gegenseitig geliked.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <a href={`/matches/${matchModal}`} className="btn-primary">
                Nachricht schreiben
              </a>
              <button
                type="button"
                onClick={() => setMatchModal(null)}
                className="btn-secondary"
              >
                Weiter swipen
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
